import { Injectable, inject } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { EntityModelField } from '../../configuration/models/entity-model-field.model';
import { Document } from '../../orders/models/document.model';
import { EntityApiEnum } from 'src/app/config/entity-api.enum';
import { Tool } from '../../orders/models/tool.model';
import { Position } from '../../orders/models/position.model';
import { Material } from '../../orders/models/material.model';
import { Operation } from '../../orders/models/operation.model';
import { EquipmentRequest } from '../../orders/models/equipment-request.model';
import { Shipping } from '../../orders/models/shipping.model';

const UNIT5_DECIMAL2 = Validators.pattern(/^\d{1,5}(\.\d{1,2})?$/);
const UNIT10_DECIMAL2 = Validators.pattern(/^\d{1,10}(\.\d{1,2})?$/);
@Injectable({
  providedIn: 'root'
})
export class FormFactoryService {
    private fb = inject(FormBuilder);

    private getCommonField(data?: any) {
        return {
        shortDescription: [data?.shortDescription, [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
        description: [data?.description , [Validators.maxLength(1000)]],
        etiquetteId: [data?.etiquetteId ?? null],
        stateId: [data?.stateId ?? 1]
    }};

    createDynamicForm(entityModelFields: EntityModelField[], jsonInfo: any): FormGroup {
        let newFb = this.fb.group({});
        if(entityModelFields && jsonInfo){
            entityModelFields.forEach((field: EntityModelField) => {
                const validators = field.required ? [Validators.required] : [];      
                newFb.addControl(field.fieldName, this.fb.control('', validators));
            });
        }
        return newFb
    }

    createCommonForm(data?: any) : FormGroup{
        return this.fb.group(this.getCommonField(data));
    }

    createEquipmentForm(data?: any) : FormGroup{
        const equipForm = {
            ...this.getCommonField(data),
             unit: [data?.unit, [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
             cost: [data?.cost, [Validators.required, UNIT5_DECIMAL2]]
            }
        return this.fb.group(equipForm);
    }

    createDocumentForm(data?:Document) : FormGroup{
        return this.fb.group({
            documentId: [data?.documentId],
            docPath: [data?.docPath, [Validators.required, Validators.minLength(3), Validators.maxLength(500)]],
        });

    }

    createMachineForm(data?: any) : FormGroup{
        const machineForm = {
            ...this.getCommonField(data),
             cost: [data?.cost, [Validators.required, UNIT5_DECIMAL2]]
            }
        return this.fb.group(machineForm);
    }

    createOperationForm(data?: any) : FormGroup{
        const machineForm = {
            ...this.getCommonField(data),
             cost: [data?.cost, [Validators.required, UNIT5_DECIMAL2]],
            materials: this.fb.array([]),
            tools: this.fb.array([]),
            }
        return this.fb.group(machineForm);
    }

    createEmployeeForm(data?: any) : FormGroup{
        const employeeForm = {
            ...this.getCommonField(data),
             cost: [data?.cost, [Validators.required, UNIT5_DECIMAL2]]
            }
        return this.fb.group(employeeForm);
    }

    createForm(entityApi: EntityApiEnum, data?: any) : FormGroup{
        switch(entityApi){
            case EntityApiEnum.EQUIPMENT:
                return this.createEquipmentForm(data);
            case EntityApiEnum.EMPLOYEE:
                return this.createEmployeeForm(data);
            case EntityApiEnum.MACHINE:
                return this.createMachineForm(data);
            case EntityApiEnum.OPERATION:
                return this.createOperationForm(data);
            default: return this.createCommonForm(data);
        }
        
    }

    createOrderForm(){
        return this.fb.group({
            shortDescription: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
            description: [null, [Validators.maxLength(1000)]],
            code: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
            etiquetteId: [null as number | null], 
            stateId: [1, [Validators.required]],
            modelId: [null as number | null],
            clientId: [null as number | null],
            reference: [null, [Validators.maxLength(150)]],
            requestDate: [new Date()],
            expireDate: [null as Date | null],
            positions: this.fb.array([])
        });
    }
    
    createWorkphaseForm() {
        return this.fb.group({
            phaseId: [null as number | null],
            positionId: [null as number | null],
            orderId: [null as number | null],
            phase: [1 as number | null],
            reference: [null as number | null],
            equipmentCost: [0, [Validators.min(0), UNIT10_DECIMAL2]],
            toolCost: [0, [Validators.min(0),UNIT10_DECIMAL2]],
            productionCost: [0, [Validators.min(0), UNIT10_DECIMAL2]],
            externalCost: [0, [Validators.min(0), UNIT10_DECIMAL2]],
            profitMarginPerc: [0],   
            totalCalculated: [{ value: 0, disabled: true }],
            total: [0, [Validators.min(0), UNIT10_DECIMAL2]],
            description: [null, [Validators.maxLength(1000)]],
            stateId: [null],
            sendDate: [null as Date | null],
            expireDate: [null as Date | null],
            operations: this.fb.array([]),
            materials: this.fb.array([]),
            tools: this.fb.array([]),
        });
    }

    createPositionForm(data?:Position) {
        return this.fb.group({
            tempId: [data?.positionId || Math.random().toString(36).substring(7)],
            positionId: [data?.positionId],
            orderId: [data?.orderId],
            stateId: [data?.stateId || 1],
            expireDate: [data?.expireDate,[Validators.required]],
            shortDescription: [data?.shortDescription, [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
            description: [data?.description, [Validators.maxLength(1000)]],
            code: [data?.code,[Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
            quantity: [data?.quantity,[Validators.min(1),Validators.required]],
            documents: this.fb.array([])
        });
    }

    createToolForm(data?: Tool): FormGroup {
        return this.fb.group({
            tempId: [{value: data?.utensileId || Math.random().toString(36).substring(7),disabled: true}],
            utensileId: [{ value: data?.utensileId, disabled: true }],
            positionId: [{ value: data?.positionId, disabled: true }],
            orderId: [{ value: data?.orderId, disabled: true }],
            
            phaseId: [data?.phaseId || 1],
            cost: [data?.cost || 0, [Validators.required, Validators.min(0)]],
            equipmentId: [data?.equipmentId,[Validators.required]],
            quantity: [data?.quantity || 0, [Validators.required, Validators.min(1)]],
            
            description: [data?.description,[Validators.maxLength(1000)]],
            });
        }


    createMaterialForm(data?: Material): FormGroup {
        return this.fb.group({
            tempId: [{value: data?.materialeId || Math.random().toString(36).substring(7),disabled: true}],
            materialeId: [{ value: data?.materialeId, disabled: true }],
            positionId: [{ value: data?.positionId, disabled: true }],
            orderId: [{ value: data?.orderId, disabled: true }],
            
            phaseId: [data?.phaseId || 1],
            cost: [data?.cost || 0, [Validators.required, Validators.min(0), UNIT10_DECIMAL2]],
            costPlaceholder: [0],
            equipmentId: [data?.equipmentId,[Validators.required]],
            quantity: [data?.quantity || 0, [Validators.required, Validators.min(1)]],
            
            description: [data?.description || '',[Validators.maxLength(1000)]],
        });
    }

    createPositionOperationForm(data?: Operation): FormGroup {
        return this.fb.group({
            tempId: [{value:data?.operationId || Math.random().toString(36).substring(7), disabled: true}],
            operationId: [{value:data?.operationId, disabled: true}],
            positionId: [{value:data?.positionId, disabled: true}],
            orderId: [{value:data?.orderId, disabled: true}],
            sequenzaId: [{value:data?.sequenzaId, disabled: true}],
            operationTypeId: [data?.operationTypeId, [Validators.required]],
            workType: [data?.workType],
            phaseId: [data?.phaseId],
            machineId: [data?.machineId],
            employeeId: [data?.employeeId],
            supplierId: [data?.supplierId],
            
            workingTime: [data?.workingTime || 0],
            equipmentTime: [data?.equipmentTime || 0],
            quantity: [data?.quantity || 1, [Validators.required, Validators.min(1)]],
            cost: [data?.cost || 0, [Validators.required, Validators.min(0)]],
            finalWorkingTime: [data?.finalWorkingTime || 0],
            finalEquipmentTime: [data?.finalEquipmentTime || 0],
            finalCost: [data?.finalCost || 0, [Validators.min(0)]],
            outcome: [data?.outcome || 0],
            shortDescription: [data?.shortDescription || '', [Validators.minLength(3),Validators.maxLength(50)]],
            description: [data?.description || '', [Validators.maxLength(1000)]],
            
            stateId: [{value:data?.stateId || null, disabled: true}],
            startDate: [data?.startDate || null],
            endDate: [data?.endDate || null]
        });
    }

    equipmentRequestForm(data?: EquipmentRequest){
        return this.fb.group({
            requestId: [{ value: data?.requestId, disabled: true }],
            orderId: [{ value: data?.orderId, disabled: true }],
            positionId: [{ value: data?.positionId, disabled: true }],
            equipmentId: [{ value: data?.equipmentId, disabled: true }],
            supplierId: [data?.supplierId || null], 
            stateId: [data?.stateId || 1, [Validators.required]], 
            description: [data?.description || '',[Validators.maxLength(1000)]],
            quantity: [data?.quantity || 1, [Validators.required, Validators.min(0)]],
            requestDate: [data?.requestDate || new Date()],
            sendDate: [data?.sendDate || null],
            extimatedDeliveryDate: [data?.extimatedDeliveryDate || null],
            deliveryDate: [data?.deliveryDate || null],
            operations: this.fb.array([])

        });
    }

    createShippingForm(data?: Shipping): FormGroup {
        return this.fb.group({
            // Identificativi e chiavi esterne (disabilitati per evitare modifiche accidentali)
            shippingId: [{value: data?.shippingId, disabled: true}],
            positionId: [{value: data?.positionId, disabled: true}],
            orderId: [{value: data?.orderId, disabled: true}],
            
            courierType: [data?.courierType || 1], // tipo corriere
            vehicleId: [data?.vehicleId || null], // ID del veicolo
            courierName: [data?.courierName || null], // Nome del corriere
            description: [data?.description || '', [Validators.maxLength(1000)]],
            
            stateId: [{value: data?.stateId || 1, disabled: true}], 
            
            weight: [data?.weight || 0, [Validators.min(0)]],
            parcel: [data?.parcel || 1, [Validators.min(1)]], // Numero di colli
            
            sendDate: [data?.sendDate || null],
            deliveryDate: [data?.deliveryDate || null]
        });
    }
}